import React, { useState, useEffect, useRef } from 'react';

const SearchableDropdown = ({ options, value, onChange, placeholder, style }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        // Initialize filtered options when options change
        setFilteredOptions(options);
    }, [options]);

    useEffect(() => {
        // Sync internal search term with external value if it matches an option
        if (value) {
            setSearchTerm(value);
        } else {
            setSearchTerm('');
        }
    }, [value]);

    useEffect(() => {
        if (isOpen) {
            const lowerCaseSearch = (searchTerm || '').toLowerCase();
            const filtered = options.filter(option =>
                (option || '').toLowerCase().includes(lowerCaseSearch)
            );
            setFilteredOptions(filtered);
            setActiveIndex(-1); // Reset highlight when filtering
        }
    }, [searchTerm, options, isOpen]);

    // Handle clicking outside to close
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                if (value) {
                    setSearchTerm(value);
                } else {
                    setSearchTerm('');
                }
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef, value]);

    // Scroll active item into view
    useEffect(() => {
        if (activeIndex >= 0 && listRef.current) {
            const activeItem = listRef.current.children[activeIndex];
            if (activeItem) {
                activeItem.scrollIntoView({
                    block: 'nearest',
                    inline: 'start'
                });
            }
        }
    }, [activeIndex]);

    const handleSelect = (option) => {
        onChange(option);
        setSearchTerm(option);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev => (prev > 0 ? prev - 1 : prev));
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[activeIndex]);
                } else if (filteredOptions.length > 0) {
                    // If no active index but items exist, maybe select the first match?
                    // handleSelect(filteredOptions[0]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                break;
            case 'Tab':
                setIsOpen(false);
                break;
            default:
                break;
        }
    };

    return (
        <div ref={wrapperRef} style={{ ...style, position: 'relative' }}>
            <input
                type="text"
                className="form-input"
                placeholder={placeholder}
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    setIsOpen(true);
                    if (searchTerm === value) {
                        setFilteredOptions(options);
                    }
                }}
                style={{ width: '100%', margin: 0, boxSizing: 'border-box' }}
            />
            {isOpen && (
                <ul
                    ref={listRef}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        zIndex: 1000,
                        borderRadius: '0 0 4px 4px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                >
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <li
                                key={index}
                                onClick={() => handleSelect(option)}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #eee',
                                    color: '#333',
                                    backgroundColor: index === activeIndex ? '#f0f0f0' : 'white'
                                }}
                                onMouseEnter={() => setActiveIndex(index)}
                            >
                                {option}
                            </li>
                        ))
                    ) : (
                        <li style={{ padding: '8px 12px', color: '#999' }}>No matches found</li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default SearchableDropdown;
